import { Button, DateTimePicker, FormField } from '@moia-dev/pace-web'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'
import { useState } from 'react'
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useWorkOrderDueAtTimeZone } from '../../utils/workOrderDueAt'

export type WorkOrderDueAtFieldProps<
  FormFieldValues extends FieldValues,
  Name extends FieldPath<FormFieldValues> = FieldPath<FormFieldValues>,
> = {
  control: Control<FormFieldValues>
  name: Name
}

/**
 * Due date and time field for the work order dialogs.
 *
 * The form value stays an absolute instant while the picker shows and reads it in
 * the selected region's timezone. Picking a date without a time yields midnight of
 * that date in the region, which is how a date-only due is stored.
 *
 * Past due instants are allowed on purpose: work orders are created and edited for
 * work that is already overdue.
 */
export const WorkOrderDueAtField = <
  FormFieldValues extends FieldValues,
  Name extends FieldPath<FormFieldValues> = FieldPath<FormFieldValues>,
>({
  control,
  name,
}: WorkOrderDueAtFieldProps<FormFieldValues, Name>) => {
  const { t } = useTranslation()
  const timeZoneId = useWorkOrderDueAtTimeZone()
  // The picker keeps the typed time in its own state and does not drop it when the
  // value goes back to empty, so clearing has to remount it.
  const [clearCount, setClearCount] = useState(0)

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value } }) => (
        <FormField label={t('common_dueAt_label')}>
          <div
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.space.Space2,
              '& input[value=""]': {
                color: theme.semantic.ColorContentDisabled,
              },
            })}
          >
            <DateTimePicker
              key={clearCount}
              css={{ flex: 1 }}
              value={value ? toZonedTime(value, timeZoneId) : undefined}
              onChange={date =>
                onChange(date ? fromZonedTime(date, timeZoneId) : null)
              }
              onBlur={onBlur}
              hideSetToNow
              size="compact"
            />
            <Button
              type="button"
              variant="plain"
              size="compact"
              label={t('common_clear_label')}
              disabled={!value}
              onClick={() => {
                onChange(null)
                setClearCount(count => count + 1)
              }}
            />
          </div>
        </FormField>
      )}
    />
  )
}
